import { get, set } from "lodash";
import { subDays, subWeeks, subMonths } from "date-fns";
import { WroWager } from "@tvg/ts-types/WroWager";
import { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ActiveBetsInfoType,
  ActiveTabEnum,
  SettledTabEnum
} from "@tvg/sh-lib-my-bets/utils/types";
import { BetStatusCodesEnum } from "@tvg/ts-types/Bet";
import {
  GroupWagersQueryProps,
  WageredGroups,
  WagerHistory,
  WagerHistoryData
} from "../types";

export const getRacesActiveBets = (wagerHistory: WagerHistory) => {
  if (wagerHistory && wagerHistory.groupWagers) {
    const activeBetsArray: number[] = [];
    wagerHistory.groupWagers.forEach((eachGroup) => {
      eachGroup.wagers.forEach((wager) => {
        if (wager.betStatus.code === "A") {
          activeBetsArray.push(wager.tvgRaceId);
        }
      });
    });

    const racesActiveBets = activeBetsArray.reduce<Record<number, number>>(
      (acc, currentValue) => {
        const value = get(acc, currentValue, 0);
        if (value > 0) {
          return {
            ...acc,
            [currentValue]: value + 1
          };
        }

        return {
          ...acc,
          [currentValue]: 1
        };
      },
      {}
    );

    return racesActiveBets;
  }
  return {};
};

const getFormattedDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getStartDate = (
  selectedSettledTab: SettledTabEnum,
  selectedTab: ActiveTabEnum
) => {
  let startDate = new Date();

  if (selectedTab === ActiveTabEnum.SETTLED) {
    const settledStartTimes = {
      TODAY: startDate,
      YESTERDAY: subDays(startDate, 1),
      LAST_WEEK: subWeeks(startDate, 1),
      LAST_MONTH: subMonths(startDate, 1)
    };

    startDate = settledStartTimes[selectedSettledTab];
  }

  return startDate;
};

const getActiveBetsInfo = (
  data: WagerHistoryData,
  selectedSettledTab: SettledTabEnum,
  selectedTab: ActiveTabEnum,
  deletedBets: string[]
): ActiveBetsInfoType => {
  const isActiveBetsTab = selectedTab === ActiveTabEnum.ACTIVE;
  const isFutureBetsTab = selectedTab === ActiveTabEnum.FUTURES;
  const startDate = getStartDate(selectedSettledTab, selectedTab);
  const groupWagers = get(data, "wagerHistory.groupWagersList.wagerGroups", []);

  let totalBets = 0;
  let totalAmount = 0;
  const betsToReturn: {
    value: string;
    wagers: WroWager[];
  }[] = [];
  const raceBets = {};

  groupWagers.forEach((eachGroup: WageredGroups) => {
    const tempActiveBets: WroWager[] = [];

    eachGroup.wagers.forEach((wager: WroWager) => {
      const shouldPushWager = isActiveBetsTab
        ? wager.betStatus.code === BetStatusCodesEnum.ACTIVE
        : wager.betStatus.code !== BetStatusCodesEnum.ACTIVE;

      const currentRaceBet: [] = get(raceBets, wager.tvgRaceId, []);
      set(raceBets, wager.tvgRaceId, [...currentRaceBet, wager]);

      if (shouldPushWager) {
        totalAmount += wager.betTotal;
        if (!deletedBets.includes(wager.serialNumber) || !isActiveBetsTab) {
          tempActiveBets.push(wager);
        }
      }
    });

    if (tempActiveBets.length > 0) {
      totalBets += tempActiveBets.length;

      const filteredActiveBets = tempActiveBets.filter((bet) => {
        if (
          selectedTab === ActiveTabEnum.ACTIVE ||
          selectedTab === ActiveTabEnum.FUTURES
        ) {
          return true;
        }

        const startDateString = startDate.toLocaleDateString();
        const postTimeDateString = new Date(
          bet.racePostTime
        ).toLocaleDateString();

        if (selectedSettledTab === SettledTabEnum.TODAY) {
          return startDateString === postTimeDateString;
        }

        return new Date(startDate) <= new Date(bet.racePostTime);
      });

      if (filteredActiveBets.length > 0) {
        filteredActiveBets.sort(
          (betA, betB) =>
            +new Date(betB.transactionDate) - +new Date(betA.transactionDate)
        );

        betsToReturn.push({
          value: eachGroup.value.replace(/#/gi, "|"),
          wagers: filteredActiveBets
        });
      }
    }
  });

  betsToReturn.sort((wagerGroupA, wagerGroupB) =>
    isActiveBetsTab
      ? +new Date(wagerGroupA.wagers[0].racePostTime) -
        +new Date(wagerGroupB.wagers[0].racePostTime)
      : +new Date(wagerGroupB.wagers[0].racePostTime) -
        +new Date(wagerGroupA.wagers[0].racePostTime)
  );

  const futureWagerList = get(data, "wagerHistory.futureWagersList.wagers", []);

  const groups = ["raceDate", "trackCode", "raceNumber"];
  // @TODO: refactor this old logic since we are pushing in the object grouped and grouped is not array type...
  const grouped = {};

  if (futureWagerList.length) {
    futureWagerList.forEach((wager: WroWager) => {
      groups
        .reduce((acc, group) => {
          // @ts-ignore
          const g = wager[group].toString();
          // @ts-ignore
          acc[g] = acc[g] || [];

          // @ts-ignore
          return acc[g];
        }, grouped)
        // @ts-ignore
        .push(wager);
    });
  }

  // @ts-ignore
  const futureWagers = [];

  Object.keys(grouped).forEach((raceDate) => {
    // @ts-ignore
    Object.keys(grouped[raceDate]).forEach((trackCode) => {
      // @ts-ignore
      Object.keys(grouped[raceDate][trackCode]).forEach((raceNumber) => {
        futureWagers.push({
          value: `${raceDate}|${trackCode}|${raceNumber}`,
          // @ts-ignore
          wagers: grouped[raceDate][trackCode][raceNumber]
        });
      });
    });
  });
  // @ts-ignore
  const betsBuffer = isFutureBetsTab ? futureWagers : betsToReturn;
  return {
    bets: betsBuffer,
    totalBets,
    totalAmount
  };
};

export default {
  apolloOptions: (props: GroupWagersQueryProps) => {
    const startDate = getStartDate(props.selectedSettledTab, props.selectedTab);

    const variables = {
      startDate: getFormattedDate(startDate),
      endDate: getFormattedDate(new Date()),
      accountId: parseInt(get(props, "accountNumber", ""), 10),
      pagination: props.hasPagination
        ? {
            current: 0,
            results: props.wagersPerPage || 20
          }
        : null,
      statusList:
        props.selectedTab.toLowerCase() === "settled" ? ["RESOLVED"] : []
    };

    return {
      skip: !props.accountNumber,
      fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
      nextFetchPolicy: "cache-first" as WatchQueryFetchPolicy,
      client: props.behgClient,
      ssr: false,
      variables
    };
  },
  getActiveBetsInfo
};
